import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { User, Mail, Building2, Unlock, Lock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// API URL - works in dev and production
const getApiUrl = () => {
  // Development environment (localhost or Emergent preview)
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('.preview.')) {
    return process.env.REACT_APP_BACKEND_URL + '/api';
  }
  // Production - PHP backend with query params
  return window.location.origin + '/api.php?route=';
};

const API_BASE = getApiUrl();

// Build API URL - handles both dev and prod
const apiUrl = (endpoint) => {
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('.preview.')) {
    return API_BASE + endpoint;
  }
  // Production: /api.php?route=leads
  return API_BASE + endpoint.replace('/', '');
};

export const LeadFormGated = ({ score, level, selectedServices, recommendations, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez entrer un email valide');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(apiUrl('/leads'), {
        ...formData,
        score,
        level,
        selected_services: selectedServices,
        recommendations
      });

      toast.success('Merci ! Découvrez vos résultats.');
      onSuccess(formData);
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in-up" data-testid="lead-form-gated">
      <div className="text-center mb-8">
        {/* oueb.it logo */}
        <img 
          src="https://oueb.it/wp-content/plugins/oueb-it-landing-page/assets/images/oueb-it-logo-primary.svg" 
          alt="oueb.it" 
          className="h-20 mx-auto mb-6"
        />
        
        <h2 className="text-2xl md:text-3xl font-bold text-[#0d2a47] font-outfit mb-3">
          Vos résultats sont prêts !
        </h2>
        <p className="text-slate-500 max-w-md mx-auto">
          On a sûrement quelques pistes à vous proposer pour vous libérer des géants du numérique... 😉
          <br />
          <span className="text-sm">Laissez-nous vos coordonnées pour découvrir tout ça !</span>
        </p>
      </div>

      {/* What you'll get */}
      <div className="mb-8 p-4 rounded-xl bg-gradient-to-br from-[#0d2a47]/5 to-[#006241]/5 border border-slate-100">
        <p className="text-sm font-medium text-[#0d2a47] mb-3">En déverrouillant vos résultats :</p>
        <ul className="space-y-2">
          {[
            'Votre score de dépendance aux GAFAM',
            'Des suggestions concrètes pour en sortir',
            'Des alternatives souveraines qui fonctionnent vraiment'
          ].map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-[#006241]" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <Card className="p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2 text-slate-700">
              <User className="w-4 h-4" />
              Prénom et Nom
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Jean Dupont"
              value={formData.name}
              onChange={handleChange}
              className="rounded-xl border-slate-200 focus:border-[#0d2a47] focus:ring-[#0d2a47]"
              data-testid="input-name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2 text-slate-700">
              <Mail className="w-4 h-4" />
              Email professionnel
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="jean.dupont@entreprise.fr"
              value={formData.email}
              onChange={handleChange}
              className="rounded-xl border-slate-200 focus:border-[#0d2a47] focus:ring-[#0d2a47]"
              data-testid="input-email"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company" className="flex items-center gap-2 text-slate-700">
              <Building2 className="w-4 h-4" />
              Entreprise / Organisation
            </Label>
            <Input
              id="company"
              name="company"
              type="text"
              placeholder="Nom de votre entreprise"
              value={formData.company}
              onChange={handleChange}
              className="rounded-xl border-slate-200 focus:border-[#0d2a47] focus:ring-[#0d2a47]"
              data-testid="input-company"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full py-6 bg-[#0d2a47] hover:bg-[#1a4a7a] text-white font-semibold"
            data-testid="submit-lead-btn"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Chargement...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Unlock className="w-4 h-4" />
                Découvrir mes résultats
              </span>
            )}
          </Button>
        </form>

        <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-100">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong>Protection de vos données :</strong> Les informations recueillies via ce formulaire sont enregistrées par oueb.it dans le but de vous contacter pour vous présenter nos solutions alternatives aux GAFAM. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition concernant vos données personnelles. Vous pouvez exercer ces droits en nous contactant à{' '}
            <a href="mailto:contact@oueb.it" className="text-[#006241] hover:underline">contact@oueb.it</a>.
            {' '}Vos données sont conservées pendant une durée maximale de 3 ans et ne sont jamais transmises à des tiers.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Consultez notre{' '}
            <a 
              href="https://oueb.it/politique-de-confidentialite/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#006241] hover:underline"
            >
              politique de confidentialité
            </a>
            {' '}pour plus d'informations.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default LeadFormGated;
